/**
 * Data Profiling Service
 * Generates automatic statistics and insights for uploaded datasets
 */

import duckdbService from './duckdbService';

const LARGE_ROW_THRESHOLD = 200000;
const PROFILE_SAMPLE_ROWS = 50000;
const MAX_PROFILE_COLUMNS = 60;

class DataProfileService {
  /**
   * Generate comprehensive profile for a dataset
   */
  async generateProfile(fileId) {
    const datasetInfo = duckdbService.getDatasetInfo(fileId);
    if (!datasetInfo) {
      throw new Error('Dataset not found');
    }

    const allColumns = datasetInfo.columns || [];
    const columns = allColumns.slice(0, MAX_PROFILE_COLUMNS);

    const profile = {
      tableName: datasetInfo.tableName,
      columns,
      totalRows: Number(datasetInfo.rowCount || 0),
      columnStats: {},
      dataQuality: {},
      suggestions: [],
      missingValues: [],
      outliers: [],
      sampled: false,
      sampleSize: 0,
      columnLimitApplied: allColumns.length > MAX_PROFILE_COLUMNS,
      totalColumns: allColumns.length
    };

    try {
      // Get total row count (reuse upload count if available)
      if (!profile.totalRows) {
        const countResult = await duckdbService.executeQuery(
          `SELECT COUNT(*) as total FROM ${datasetInfo.tableName}`,
          fileId
        );
        profile.totalRows = Number(countResult.result?.[0]?.total || 0);
      }

      profile.sampled = profile.totalRows > LARGE_ROW_THRESHOLD;
      profile.sampleSize = profile.sampled
        ? Math.min(PROFILE_SAMPLE_ROWS, profile.totalRows)
        : profile.totalRows;

      let schemaInfo = {};
      try {
        schemaInfo = await this.getSchemaInfo(fileId, datasetInfo.tableName);
      } catch (error) {
        console.warn('Failed to load schema info:', error);
      }

      // Get column statistics
      for (const column of columns) {
        const columnSchema = schemaInfo[column] || null;
        profile.columnStats[column] = await this.getColumnStats(
          fileId,
          datasetInfo.tableName,
          column,
          columnSchema,
          {
            sampled: profile.sampled,
            sampleSize: profile.sampleSize
          }
        );
      }

      // Calculate data quality metrics
      profile.dataQuality = this.calculateDataQuality(profile);

      // Generate suggestions
      profile.suggestions = this.generateSuggestions(profile);

      // Missing values summary
      profile.missingValues = this.getMissingValuesSummary(profile);

      // Outlier summary
      profile.outliers = this.getOutlierSummary(profile);

    } catch (error) {
      console.error('Error generating profile:', error);
    }

    return profile;
  }

  /**
   * Get statistics for a single column
   */
  async getColumnStats(fileId, tableName, column, schemaInfo = null, options = {}) {
    const { sampled, sampleSize } = options;
    const stats = {
      column,
      type: 'unknown',
      nullCount: 0,
      uniqueCount: 0,
      topValues: [],
      numericStats: null,
      distribution: null,
      outlierStats: null,
      dbType: schemaInfo?.dbType || 'unknown',
      nullable: schemaInfo?.nullable ?? null,
      sampled: Boolean(sampled),
      sampleSize: sampled ? sampleSize : null
    };

    try {
      const fromClause = sampled
        ? `FROM (SELECT * FROM ${tableName} LIMIT ${sampleSize}) AS sample`
        : `FROM ${tableName}`;

      // Get null count and unique count
      const basicStats = await duckdbService.executeQuery(
        `SELECT 
          COUNT(*) - COUNT("${column}") as null_count,
          COUNT(DISTINCT "${column}") as unique_count,
          COUNT(*) as total
        ${fromClause}`,
        fileId
      );

      if (basicStats.result?.[0]) {
        stats.nullCount = basicStats.result[0].null_count || 0;
        stats.uniqueCount = basicStats.result[0].unique_count || 0;
        stats.total = basicStats.result[0].total || 0;
        stats.nullPercentage = stats.total > 0 ? ((stats.nullCount / stats.total) * 100).toFixed(1) : 0;
      }

      // Try to get top values
      try {
        const topValues = await duckdbService.executeQuery(
          `SELECT "${column}" as value, COUNT(*) as count 
           ${fromClause} 
           WHERE "${column}" IS NOT NULL 
           GROUP BY "${column}" 
           ORDER BY count DESC 
           LIMIT 5`,
          fileId
        );
        stats.topValues = topValues.result || [];
      } catch (e) {
        // Column might have issues with grouping
      }

      // Try to get numeric stats
      try {
        const numericStats = await duckdbService.executeQuery(
          `SELECT 
            MIN("${column}") as min_val,
            MAX("${column}") as max_val,
            AVG(TRY_CAST("${column}" AS DOUBLE)) as avg_val,
            MEDIAN(TRY_CAST("${column}" AS DOUBLE)) as median_val
          ${fromClause}`,
          fileId
        );

        if (numericStats.result?.[0]?.avg_val !== null) {
          stats.type = 'numeric';
          stats.numericStats = {
            min: numericStats.result[0].min_val,
            max: numericStats.result[0].max_val,
            avg: numericStats.result[0].avg_val?.toFixed(2),
            median: numericStats.result[0].median_val?.toFixed(2)
          };
          stats.outlierStats = await this.getOutlierStats(fileId, tableName, column, sampled, sampleSize);
          const nonNullCount = Math.max(0, (stats.total || 0) - (stats.nullCount || 0));
          stats.distribution = await this.getNumericDistribution(
            fileId,
            tableName,
            column,
            stats.numericStats,
            nonNullCount,
            sampled,
            sampleSize
          );
        } else {
          stats.type = stats.uniqueCount < 20 ? 'categorical' : 'text';
        }
      } catch (e) {
        stats.type = stats.uniqueCount < 20 ? 'categorical' : 'text';
      }

    } catch (error) {
      console.error(`Error getting stats for column ${column}:`, error);
    }

    return stats;
  }

  async getOutlierStats(fileId, tableName, column, sampled, sampleSize) {
    try {
      const fromClause = sampled
        ? `FROM (SELECT * FROM ${tableName} LIMIT ${sampleSize}) AS sample`
        : `FROM ${tableName}`;

      const result = await duckdbService.executeQuery(
        `WITH values AS (
          SELECT TRY_CAST("${column}" AS DOUBLE) AS v
          ${fromClause}
          WHERE "${column}" IS NOT NULL
        ),
        stats AS (
          SELECT AVG(v) AS avg_val, STDDEV_POP(v) AS std_val
          FROM values
        )
        SELECT
          ANY_VALUE(stats.avg_val) AS avg_val,
          ANY_VALUE(stats.std_val) AS std_val,
          SUM(
            CASE
              WHEN stats.std_val IS NULL OR stats.std_val = 0 THEN 0
              WHEN v < stats.avg_val - 3 * stats.std_val OR v > stats.avg_val + 3 * stats.std_val THEN 1
              ELSE 0
            END
          ) AS outlier_count,
          COUNT(*) AS total
        FROM values, stats`,
        fileId
      );

      const row = result.result?.[0];
      if (!row) return null;
      const total = Number(row.total || 0);
      const outlierCount = Number(row.outlier_count || 0);
      return {
        count: outlierCount,
        percentage: total > 0 ? Number(((outlierCount / total) * 100).toFixed(1)) : 0
      };
    } catch (error) {
      return null;
    }
  }

  async getNumericDistribution(fileId, tableName, column, numericStats, nonNullCount = 0, sampled, sampleSize) {
    if (!numericStats) return null;

    const minVal = Number(numericStats.min);
    const maxVal = Number(numericStats.max);
    if (Number.isNaN(minVal) || Number.isNaN(maxVal)) return null;

    if (minVal === maxVal) {
      return {
        buckets: [{ label: `${minVal}`, count: nonNullCount }],
        min: minVal,
        max: maxVal
      };
    }

    const bucketCount = 5;

    try {
      const fromClause = sampled
        ? `FROM (SELECT * FROM ${tableName} LIMIT ${sampleSize}) AS sample`
        : `FROM ${tableName}`;

      const result = await duckdbService.executeQuery(
        `WITH values AS (
          SELECT TRY_CAST("${column}" AS DOUBLE) AS v
          ${fromClause}
          WHERE "${column}" IS NOT NULL
        ),
        bounds AS (
          SELECT MIN(v) AS min_val, MAX(v) AS max_val
          FROM values
        ),
        bucketed AS (
          SELECT
            LEAST(${bucketCount - 1}, GREATEST(0, CAST(FLOOR((v - bounds.min_val) / NULLIF(bounds.max_val - bounds.min_val, 0) * ${bucketCount}) AS INTEGER))) AS bucket,
            v
          FROM values
          CROSS JOIN bounds
        )
        SELECT bucket, COUNT(*) as count
        FROM bucketed
        GROUP BY bucket
        ORDER BY bucket`,
        fileId
      );

      const buckets = (result.result || []).map((row) => ({
        bucket: Number(row.bucket),
        count: Number(row.count || 0)
      }));

      const bucketSize = (maxVal - minVal) / bucketCount;
      const labeledBuckets = [];
      for (let i = 0; i < bucketCount; i += 1) {
        const start = minVal + bucketSize * i;
        const end = i === bucketCount - 1 ? maxVal : minVal + bucketSize * (i + 1);
        const count = buckets.find((item) => item.bucket === i)?.count || 0;
        labeledBuckets.push({
          label: `${start.toFixed(2)} - ${end.toFixed(2)}`,
          count
        });
      }

      return { buckets: labeledBuckets, min: minVal, max: maxVal };
    } catch (error) {
      return null;
    }
  }

  /**
   * Get schema info (DuckDB types) for a table
   */
  async getSchemaInfo(fileId, tableName) {
    const schema = {};
    const result = await duckdbService.executeQuery(
      `DESCRIBE ${tableName}`,
      fileId
    );

    (result.result || []).forEach((row) => {
      const name = row.column_name || row.column || row.name || row.columnName;
      if (!name) return;
      const dbType = row.column_type || row.type || row.data_type || row.columnType || 'unknown';
      const nullableRaw = row.null || row.nullable || row.is_nullable || row.isNullable;
      let nullable = null;
      if (nullableRaw !== undefined) {
        const normalized = String(nullableRaw).toLowerCase();
        nullable = normalized === 'yes' || normalized === 'true' || normalized === '1';
      }
      schema[name] = { dbType, nullable };
    });

    return schema;
  }

  /**
   * Calculate overall data quality metrics
   */
  calculateDataQuality(profile) {
    const quality = {
      score: 100,
      issues: [],
      completeness: 100,
      uniqueness: 0
    };

    let totalNulls = 0;
    let totalCells = 0;

    for (const [column, stats] of Object.entries(profile.columnStats)) {
      totalNulls += stats.nullCount || 0;
      totalCells += profile.totalRows;

      // Check for high null percentage
      if (stats.nullPercentage > 20) {
        quality.issues.push({
          type: 'missing_data',
          severity: stats.nullPercentage > 50 ? 'high' : 'medium',
          column,
          message: `${column} has ${stats.nullPercentage}% missing values`
        });
      }

      // Check for potential duplicates
      if (stats.uniqueCount === 1 && profile.totalRows > 1) {
        quality.issues.push({
          type: 'constant_value',
          severity: 'low',
          column,
          message: `${column} has only one unique value`
        });
      }
    }

    // Calculate overall completeness
    quality.completeness = totalCells > 0 
      ? (((totalCells - totalNulls) / totalCells) * 100).toFixed(1)
      : 100;

    // Calculate quality score
    quality.score = Math.max(0, 100 - (quality.issues.length * 10) - ((100 - quality.completeness) * 0.5));

    return quality;
  }

  /**
   * Generate smart suggestions based on profile
   */
  generateSuggestions(profile) {
    const suggestions = [];

    // Find categorical columns for grouping
    const categoricalCols = Object.entries(profile.columnStats)
      .filter(([_, stats]) => stats.type === 'categorical' || stats.uniqueCount < 20)
      .map(([col]) => col);

    // Find numeric columns for aggregation
    const numericCols = Object.entries(profile.columnStats)
      .filter(([_, stats]) => stats.type === 'numeric' && stats.numericStats)
      .map(([col]) => col);

    if (categoricalCols.length > 0 && numericCols.length > 0) {
      suggestions.push({
        type: 'aggregation',
        question: `What is the average ${numericCols[0]} by ${categoricalCols[0]}?`,
        description: 'Compare averages across categories'
      });
    }

    if (categoricalCols.length > 0) {
      suggestions.push({
        type: 'distribution',
        question: `Show me the distribution of ${categoricalCols[0]}`,
        description: 'See how records are distributed'
      });
    }

    if (numericCols.length > 0) {
      suggestions.push({
        type: 'ranking',
        question: `What are the top 10 records by ${numericCols[0]}?`,
        description: 'Find highest values'
      });
    }

    // Always suggest basic exploration
    suggestions.push({
      type: 'explore',
      question: 'Show me the first 20 rows',
      description: 'Preview the data'
    });

    suggestions.push({
      type: 'count',
      question: 'How many total records are there?',
      description: 'Get total count'
    });

    return suggestions;
  }

  getMissingValuesSummary(profile) {
    if (!profile?.columnStats) return [];

    return Object.entries(profile.columnStats)
      .map(([column, stats]) => ({
        column,
        nullCount: stats.nullCount || 0,
        nullPercentage: Number(stats.nullPercentage || 0)
      }))
      .filter((item) => item.nullCount > 0)
      .sort((a, b) => b.nullPercentage - a.nullPercentage)
      .slice(0, 5);
  }

  getOutlierSummary(profile) {
    if (!profile?.columnStats) return [];

    return Object.entries(profile.columnStats)
      .map(([column, stats]) => ({
        column,
        outlierCount: stats.outlierStats?.count || 0,
        outlierPercentage: stats.outlierStats?.percentage || 0
      }))
      .filter((item) => item.outlierCount > 0)
      .sort((a, b) => b.outlierPercentage - a.outlierPercentage)
      .slice(0, 5);
  }

  /**
   * Generate suggestions based on schema types only
   */
  async generateSchemaSuggestions(fileId) {
    const datasetInfo = duckdbService.getDatasetInfo(fileId);
    if (!datasetInfo) return [];

    const schema = await this.getSchemaInfo(fileId, datasetInfo.tableName);
    const numericCols = [];
    const categoricalCols = [];
    const dateCols = [];

    datasetInfo.columns.forEach((column) => {
      const dbType = (schema[column]?.dbType || '').toLowerCase();
      if (dbType.includes('date') || dbType.includes('time')) {
        dateCols.push(column);
        return;
      }
      if (dbType.includes('int') || dbType.includes('double') || dbType.includes('decimal') || dbType.includes('numeric') || dbType.includes('real')) {
        numericCols.push(column);
        return;
      }
      categoricalCols.push(column);
    });

    const suggestions = [];

    if (numericCols.length > 0 && categoricalCols.length > 0) {
      suggestions.push({
        type: 'aggregation',
        question: `What is the average ${numericCols[0]} by ${categoricalCols[0]}?`,
        description: 'Compare numeric values across categories'
      });
    }

    if (numericCols.length > 0 && dateCols.length > 0) {
      suggestions.push({
        type: 'trend',
        question: `Show ${numericCols[0]} over time by ${dateCols[0]}`,
        description: 'Identify trends over time'
      });
    }

    if (categoricalCols.length > 0) {
      suggestions.push({
        type: 'distribution',
        question: `Show me the distribution of ${categoricalCols[0]}`,
        description: 'See how records are distributed'
      });
    }

    if (numericCols.length > 0) {
      suggestions.push({
        type: 'ranking',
        question: `What are the top 10 records by ${numericCols[0]}?`,
        description: 'Find highest values'
      });
    }

    suggestions.push({
      type: 'explore',
      question: 'Show me the first 20 rows',
      description: 'Preview the data'
    });

    suggestions.push({
      type: 'count',
      question: 'How many total records are there?',
      description: 'Get total count'
    });

    return suggestions;
  }

  /**
   * Get quick stats summary
   */
  async getQuickSummary(fileId) {
    const datasetInfo = duckdbService.getDatasetInfo(fileId);
    if (!datasetInfo) return null;

    try {
      const countResult = await duckdbService.executeQuery(
        `SELECT COUNT(*) as total FROM ${datasetInfo.tableName}`,
        fileId
      );

      return {
        tableName: datasetInfo.tableName,
        totalRows: countResult.result?.[0]?.total || 0,
        columnCount: datasetInfo.columns.length,
        columns: datasetInfo.columns
      };
    } catch (error) {
      console.error('Error getting quick summary:', error);
      return null;
    }
  }
}

const dataProfileService = new DataProfileService();
export default dataProfileService;
